// NOTE: import from the `/design-ontology-binding` subpath, not the bare
// `@cogo/agentic-generator` package root. The root barrel value-exports
// `generateBlock` (./generate-block) and `validateUi/validateEvents/validateFlow`
// (./validator-adapter.js), both of which value-import the root `@cogo/validator`
// (not `/browser`) and transitively reach `io/loaders/block-loader.ts`'s
// top-level `node:fs` import. Vite does not tree-shake dev-server ESM, so a
// bare-root import here crashes Studio's client boot (`Module "node:fs" has
// been externalized for browser compatibility`), which cascades into every
// Studio browser proof failing to find `grouped-rail`. See
// packages/cogo-agentic-generator/src/design-ontology-binding/index.ts.
import {
  admitApplicationCompositionInputs,
  admitLibraryComposition,
} from '@cogo/agentic-generator/design-ontology-binding'
import { isCanonicalSourceInteractionRelationSet } from '@cogo/agentic-generator/source-interaction-relation-set-authority'
import { hashCanonicalContent } from '@cogo/schema/canonical-content-hash'
import { getComponentEventCapability } from '@cogo/schema/component-event-capabilities'
import type { LibraryRagOrchestrationResult } from '@cogo/agentic-generator/library-rag-agent-orchestration'
import { classifyStudioLibraryRagRuntimeAuthority } from './studio-library-rag-runtime-authority'

type SuccessfulLibraryRagReceipt = Extract<
  LibraryRagOrchestrationResult,
  { ok: true }
>
type SuccessfulLibraryRagArtifact = SuccessfulLibraryRagReceipt['artifact']
type SuccessfulLibraryArtifact = SuccessfulLibraryRagArtifact['library']
type SuccessfulCompiledDesign = SuccessfulLibraryRagArtifact['design']
type SuccessfulLibraryRagEvidence = SuccessfulLibraryRagReceipt['evidence']

export type StudioLibraryRagReceiptGapCode =
  | 'receipt-invalid'
  | 'application-blueprint-ref-mismatch'
  | 'ux-pattern-refs-mismatch'
  | 'design-release-ref-mismatch'
  | 'normalized-intent-mismatch'
  | 'critic-verdict-not-pass'
  | 'fallback-trace-open'
  | 'compiler-artifact-mismatch'

export type StudioLibraryRagReceiptValidation =
  | { ok: true; receipt: SuccessfulLibraryRagReceipt }
  | { ok: false; gapCode: StudioLibraryRagReceiptGapCode }

const UNSAFE_RAG_RECEIPT_PATTERN =
  /(?:authorization\s*:|bearer\s+[a-z0-9._~+/-]+|(?:api[_-]?key|client[_-]?secret|access[_-]?token|password)\s*[:=]|(?:https?|wss?|ftp):\/\/|\bsk-[a-z0-9_-]{12,}|\beyj[a-z0-9_-]{8,}\.[a-z0-9_-]{8,}\.[a-z0-9_-]{8,})/i

const ACTION_ROLES = new Set([
  'primary',
  'secondary',
  'recovery',
  'destructive',
])
const PORT_KINDS = new Set(['data', 'event', 'action'])
const PORT_DIRECTIONS = new Set(['in', 'out'])
const STATUS_INTENTS = new Set([
  'idle',
  'pending',
  'success',
  'error',
  'retry',
  'cancelled',
])
const JOURNEY_PATHS = new Set([
  'forward',
  'back',
  'cancel',
  'retry',
  'recovery',
])

/** The only admission gates a retrieval provenance envelope may name. */
const KNOWLEDGE_SHARD_ADMISSION_GATES = new Set([
  'admitUxApplicationLibraryOntology',
  'admitDesignBindingOntology',
])
/** The only roots one admitted shard envelope may declare. */
const KNOWLEDGE_SHARD_ROOT_KINDS = new Set([
  'application-blueprint',
  'design-ontology-release',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function hasExactKeys(
  value: unknown,
  keys: readonly string[],
): value is Record<string, unknown> {
  if (!isRecord(value)) return false
  const own = Object.keys(value)
  return own.length === keys.length && keys.every((key) => own.includes(key))
}

function isText(value: unknown, maxLength = 8_192): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maxLength
  )
}

function isSafeReceiptText(value: unknown, maxLength: number): value is string {
  return (
    isText(value, maxLength) &&
    !UNSAFE_RAG_RECEIPT_PATTERN.test(value)
  )
}

function isSafeReceiptIdentifier(
  value: unknown,
  maxLength: number,
): value is string {
  return (
    isSafeReceiptText(value, maxLength) &&
    /^[A-Za-z0-9][A-Za-z0-9._:/@+-]*$/.test(value) &&
    !value.includes('://')
  )
}

function isStringArray(
  value: unknown,
  maxItems = 4_096,
  maxLength = 8_192,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= maxItems &&
    value.every((item) => isText(item, maxLength))
  )
}

function isSafeReceiptIdentifierArray(
  value: unknown,
  maxItems: number,
  maxLength: number,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= maxItems &&
    value.every((item) => isSafeReceiptIdentifier(item, maxLength))
  )
}

function isSafeReceiptTextArray(
  value: unknown,
  maxItems: number,
  maxLength: number,
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length <= maxItems &&
    value.every((item) => isSafeReceiptText(item, maxLength))
  )
}

function isRecordArray(
  value: unknown,
  predicate: (item: unknown) => boolean,
  maxItems = 4_096,
): boolean {
  return (
    Array.isArray(value) &&
    value.length <= maxItems &&
    value.every(predicate)
  )
}

function isReceiptHash(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^(?:sha256:)?[a-f0-9]{64}$/i.test(value)
  )
}

function isSemanticContent(value: unknown): boolean {
  if (!isRecord(value)) return false
  const allowedKeys = new Set([
    'title',
    'description',
    'label',
    'location',
    'pendingLabel',
    'successLabel',
    'errorLabel',
  ])
  return Object.entries(value).every(
    ([key, item]) =>
      allowedKeys.has(key) &&
      (typeof item === 'string' ||
        typeof item === 'number' ||
        typeof item === 'boolean') &&
      (typeof item !== 'number' || Number.isFinite(item)),
  )
}

function isPlannedAction(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.role === 'string' &&
    ACTION_ROLES.has(value.role) &&
    isText(value.capabilityRef) &&
    isText(value.slotRef)
  )
}

function isCompiledPort(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.portId) &&
    typeof value.kind === 'string' &&
    PORT_KINDS.has(value.kind) &&
    typeof value.direction === 'string' &&
    PORT_DIRECTIONS.has(value.direction) &&
    isText(value.valueType) &&
    typeof value.required === 'boolean' &&
    (value.resultId === undefined || isText(value.resultId))
  )
}

function isResolvedBinding(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.bindingId) &&
    isText(value.sourcePortId) &&
    isText(value.targetPortId)
  )
}

function isCompiledFlow(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.flowId) &&
    isText(value.eventId) &&
    isText(value.capabilityRef)
  )
}

function isCompiledDatasource(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.datasourceId) &&
    isText(value.capabilityRef)
  )
}

function isCompiledState(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.stateId) &&
    typeof value.statusIntent === 'string' &&
    STATUS_INTENTS.has(value.statusIntent)
  )
}

function isCompiledTransition(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.transitionId) &&
    isText(value.fromStateId) &&
    isText(value.eventId) &&
    isText(value.toStateId) &&
    isStringArray(value.effectActionIds) &&
    isStringArray(value.emittedEventIds)
  )
}

function isCompiledStatechart(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.uxInstanceId) &&
    isText(value.namespaceId) &&
    isText(value.definitionRef) &&
    isText(value.intent) &&
    isText(value.actorContext) &&
    isStringArray(value.requiredCapabilities) &&
    isStringArray(value.preconditions) &&
    isStringArray(value.postconditions) &&
    isRecordArray(value.states, isCompiledState) &&
    isStringArray(value.eventIds) &&
    isRecordArray(value.ports, isCompiledPort) &&
    isRecordArray(value.transitions, isCompiledTransition)
  )
}

function isCompiledJourneyTransition(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.eventId) &&
    isText(value.toStepId) &&
    typeof value.path === 'string' &&
    JOURNEY_PATHS.has(value.path)
  )
}

function isCompiledJourneyStep(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.stepId) &&
    isStringArray(value.uxInstanceNamespaceIds) &&
    isStringArray(value.pageIds) &&
    isRecordArray(value.transitions, isCompiledJourneyTransition)
  )
}

function isCompiledJourney(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.entryStepId) &&
    isStringArray(value.terminalStepIds) &&
    isRecordArray(value.steps, isCompiledJourneyStep)
  )
}

function isRuntimePage(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.pageId) &&
    isText(value.journeyStepId) &&
    isText(value.uxInstanceNamespaceId)
  )
}

function isRuntimeBlock(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.blockId) &&
    isText(value.pageId) &&
    isText(value.uxInstanceNamespaceId) &&
    isText(value.semanticNodeId)
  )
}

function isSemanticAction(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.actionId) &&
    isText(value.blockId) &&
    isText(value.uxInstanceNamespaceId) &&
    isPlannedAction(value.declaration)
  )
}

function isResultResource(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.resultId) &&
    isText(value.portId) &&
    isText(value.ownerScopeId)
  )
}

function isLibraryRuntime(value: unknown): boolean {
  return (
    isRecord(value) &&
    isSafeReceiptText(value.userGoal, 4_096) &&
    isText(value.actor) &&
    isStringArray(value.capabilities) &&
    isStringArray(value.applicationStateIds) &&
    isStringArray(value.applicationEventIds) &&
    isRecordArray(value.applicationFlows, isCompiledFlow) &&
    isRecordArray(value.datasources, isCompiledDatasource) &&
    isRecordArray(value.applicationPorts, isCompiledPort) &&
    isRecordArray(value.uxStatecharts, isCompiledStatechart) &&
    isCompiledJourney(value.journey) &&
    isRecordArray(value.portBindings, isResolvedBinding) &&
    isRecordArray(value.pages, isRuntimePage) &&
    isRecordArray(value.blocks, isRuntimeBlock) &&
    isRecordArray(value.semanticActions, isSemanticAction) &&
    isRecordArray(value.results, isResultResource) &&
    isStringArray(value.pageIds) &&
    isStringArray(value.blockIds) &&
    isStringArray(value.actionIds) &&
    isStringArray(value.resultIds)
  )
}

function isScopeManifestEntry(value: unknown): boolean {
  return (
    isRecord(value) &&
    (value.scopeKind === 'application' || value.scopeKind === 'ux-instance') &&
    isText(value.ownerRef) &&
    isText(value.namespaceId)
  )
}

function isLibraryArtifact(
  value: unknown,
): value is SuccessfulLibraryArtifact {
  return (
    isRecord(value) &&
    value.schemaVersion === 'cogo.library-composition-artifact.v1' &&
    isText(value.applicationBlueprintRef) &&
    isText(value.applicationInstanceId) &&
    isRecordArray(value.scopeManifest, isScopeManifestEntry) &&
    isRecordArray(value.resolvedBindings, isResolvedBinding) &&
    isStringArray(value.resolvedApplicationFlowIds) &&
    isLibraryRuntime(value.runtime) &&
    isRecord(value.plan) &&
    isRecord(value.provenance) &&
    isText(value.provenance.applicationCoreRef) &&
    isText(value.provenance.applicationBlueprintRef) &&
    isStringArray(value.provenance.uxPatternRefs) &&
    isStringArray(value.provenance.sourceRefs) &&
    Array.isArray(value.provenance.legacyRefs) &&
    value.provenance.legacyRefs.length === 0 &&
    isRecord(value.generationTrace) &&
    value.generationTrace.mode === 'deterministic-library-composition' &&
    typeof value.generationTrace.legacyStudioArtifactUsed === 'boolean' &&
    typeof value.generationTrace.genericFallbackUsed === 'boolean' &&
    typeof value.generationTrace.rawPresentationAuthoredByModel === 'boolean'
  )
}

function isCompiledDesignAction(value: unknown): boolean {
  return (
    isPlannedAction(value) &&
    isRecord(value) &&
    isText(value.tokenRef) &&
    isText(value.exclusiveGroup)
  )
}

function isCompiledDesignNode(value: unknown): boolean {
  return (
    isRecord(value) &&
    isText(value.nodeId) &&
    isText(value.componentRef) &&
    isText(value.rendererRef) &&
    isText(value.regionRef) &&
    isSemanticContent(value.content) &&
    isStringArray(value.visibleIn) &&
    isStringArray(value.tokenRefs) &&
    (value.action === undefined || isCompiledDesignAction(value.action)) &&
    isRecord(value.provenance) &&
    isText(value.provenance.planRef) &&
    isText(value.provenance.releaseRef) &&
    isText(value.provenance.recipeRef)
  )
}

function isCompiledDesign(
  value: unknown,
): value is SuccessfulCompiledDesign {
  return (
    isRecord(value) &&
    value.schemaVersion === 'cogo.compiled-application-composition.v1' &&
    isText(value.planId) &&
    isText(value.applicationCoreRef) &&
    value.target === 'web' &&
    isText(value.locale, 64) &&
    isText(value.designReleaseRef) &&
    isText(value.recipeRef) &&
    isStringArray(value.states) &&
    isRecordArray(value.nodes, isCompiledDesignNode) &&
    isRecord(value.generationTrace) &&
    value.generationTrace.mode === 'deterministic-design-composition' &&
    typeof value.generationTrace.rawPresentationAuthoredByModel === 'boolean' &&
    typeof value.generationTrace.genericFallbackUsed === 'boolean' &&
    typeof value.generationTrace.generatedOutputAdmissionAllowed === 'boolean'
  )
}

function hasCanonicalSourceBoundInteractions(
  value: unknown,
  sourceInteractionRelations: unknown,
  artifact: {
    library: SuccessfulLibraryArtifact
    design: SuccessfulCompiledDesign
  },
): boolean {
  if (
    sourceInteractionRelations !== undefined &&
    !isRecordArray(
      sourceInteractionRelations,
      isSourceInteractionRelation,
      256,
    )
  ) {
    return false
  }
  const relations = sourceInteractionRelations ?? []
  if (!Array.isArray(relations) || !Array.isArray(value)) return false
  const expected: Record<string, unknown>[] = []
  const seen = new Set<string>()

  for (const relation of relations) {
    if (!isSourceInteractionRelation(relation)) return false
    const block = artifact.library.runtime.blocks.find(
      (candidate) => candidate.blockId === relation.blockId,
    )
    const designNode = artifact.design.nodes.find(
      (candidate) => candidate.nodeId === relation.sourceNodeId,
    )
    const statechart = artifact.library.runtime.uxStatecharts.find(
      (candidate) =>
        candidate.namespaceId === block?.uxInstanceNamespaceId,
    )
    const transition = statechart?.transitions.find(
      (candidate) => candidate.transitionId === relation.flowId,
    )
    const componentType = designNode
      ? rendererComponentType(designNode.rendererRef)
      : ''
    const capability = getComponentEventCapability(componentType)
    if (
      !block ||
      !designNode ||
      !statechart ||
      !transition ||
      block.semanticNodeId !== relation.sourceNodeId ||
      relation.elementId !== relation.sourceNodeId ||
      designNode.componentRef !== relation.componentRef ||
      !capability?.events.some(
        (eventType) => eventType === relation.eventType,
      )
    ) {
      return false
    }
    const duplicateKey = [
      relation.blockId,
      relation.elementId,
      relation.eventType,
    ].join('\u0000')
    if (seen.has(duplicateKey)) return false
    seen.add(duplicateKey)
    expected.push({
      blockId: relation.blockId,
      sourceNodeId: relation.sourceNodeId,
      elementId: relation.elementId,
      componentRef: relation.componentRef,
      eventType: relation.eventType,
      flowId: relation.flowId,
      stateWrites: [],
      provenance: {
        uxInstanceNamespaceId: statechart.namespaceId,
        uxPatternRef: statechart.definitionRef,
        transitionId: transition.transitionId,
        componentCapabilityType: componentType,
        effectActionIds: [...transition.effectActionIds],
        emittedEventIds: [...transition.emittedEventIds],
      },
    })
  }

  expected.sort(compareSourceInteraction)
  try {
    return hashCanonicalContent(value) === hashCanonicalContent(expected)
  } catch {
    return false
  }
}

function isSourceInteractionRelation(
  value: unknown,
): value is Record<
  | 'blockId'
  | 'sourceNodeId'
  | 'elementId'
  | 'componentRef'
  | 'eventType'
  | 'flowId',
  string
> {
  return (
    hasExactKeys(value, [
      'blockId',
      'sourceNodeId',
      'elementId',
      'componentRef',
      'eventType',
      'flowId',
    ]) &&
    isText(value.blockId, 2_048) &&
    isText(value.sourceNodeId, 2_048) &&
    isText(value.elementId, 2_048) &&
    isText(value.componentRef, 2_048) &&
    isText(value.eventType, 128) &&
    isText(value.flowId, 2_048)
  )
}

function rendererComponentType(rendererRef: string): string {
  const localName = rendererRef.slice(rendererRef.lastIndexOf(':') + 1)
  return localName
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .toLowerCase()
}

function compareSourceInteraction(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): number {
  return (
    String(left.blockId).localeCompare(String(right.blockId)) ||
    String(left.elementId).localeCompare(String(right.elementId)) ||
    String(left.eventType).localeCompare(String(right.eventType)) ||
    String(left.flowId).localeCompare(String(right.flowId))
  )
}

function equalStringArrays(left: string[], right: unknown): boolean {
  return (
    isStringArray(right) &&
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  )
}

function isSourceBoundInteractionContract(
  value: unknown,
  artifact: {
    designOntologyReleaseRef: string
    applicationSemanticHash: string
    designBindingHash: string
    renderContractHash: string
    library: SuccessfulLibraryArtifact
    design: SuccessfulCompiledDesign
  },
  sourceInteractionRelations: unknown,
): boolean {
  return (
    hasExactKeys(value, [
      'schemaVersion',
      'authorityRef',
      'contentHash',
      'interactions',
      'provenance',
    ]) &&
    value.schemaVersion === 'cogo.source-bound-interaction-contract.v1' &&
    value.authorityRef ===
      `${artifact.library.applicationBlueprintRef}#source-bound-interactions` &&
    typeof value.contentHash === 'string' &&
    /^sha256:[a-f0-9]{64}$/.test(value.contentHash) &&
    hasMatchingSourceBoundContentHash(value) &&
    hasCanonicalSourceBoundInteractions(
      value.interactions,
      sourceInteractionRelations,
      artifact,
    ) &&
    hasExactKeys(value.provenance, [
      'compiler',
      'applicationBlueprintRef',
      'applicationInstanceId',
      'designOntologyReleaseRef',
      'applicationSemanticHash',
      'designBindingHash',
      'renderContractHash',
      'uxPatternRefs',
      'sourceRefs',
    ]) &&
    value.provenance.compiler ===
      'cogo.library-design-source-bound-interaction-compiler.v1' &&
    value.provenance.applicationBlueprintRef ===
      artifact.library.applicationBlueprintRef &&
    value.provenance.applicationInstanceId ===
      artifact.library.applicationInstanceId &&
    value.provenance.designOntologyReleaseRef ===
      artifact.designOntologyReleaseRef &&
    value.provenance.applicationSemanticHash ===
      artifact.applicationSemanticHash &&
    value.provenance.designBindingHash === artifact.designBindingHash &&
    value.provenance.renderContractHash === artifact.renderContractHash &&
    equalStringArrays(
      artifact.library.provenance.uxPatternRefs,
      value.provenance.uxPatternRefs,
    ) &&
    equalStringArrays(
      artifact.library.provenance.sourceRefs,
      value.provenance.sourceRefs,
    )
  )
}

function hasMatchingSourceBoundContentHash(
  value: Record<string, unknown>,
): boolean {
  try {
    const { contentHash, ...body } = value
    return hashCanonicalContent(body) === contentHash
  } catch {
    return false
  }
}

function isDesignBoundArtifact(
  value: unknown,
  sourceInteractionRelations: unknown,
): value is SuccessfulLibraryRagArtifact {
  return (
    isRecord(value) &&
    value.schemaVersion === 'cogo.design-bound-application-artifact.v1' &&
    isText(value.designOntologyReleaseRef) &&
    isLibraryArtifact(value.library) &&
    isCompiledDesign(value.design) &&
    isReceiptHash(value.applicationSemanticHash) &&
    isReceiptHash(value.designBindingHash) &&
    isReceiptHash(value.renderContractHash) &&
    isSourceBoundInteractionContract(value.sourceBoundInteractionContract, {
      designOntologyReleaseRef: value.designOntologyReleaseRef,
      applicationSemanticHash: value.applicationSemanticHash,
      designBindingHash: value.designBindingHash,
      renderContractHash: value.renderContractHash,
      library: value.library,
      design: value.design,
    }, sourceInteractionRelations) &&
    isRecord(value.generationTrace) &&
    value.generationTrace.mode === 'mandatory-design-ontology-binding' &&
    value.generationTrace.designOntologyRequired === true &&
    typeof value.generationTrace.genericFallbackUsed === 'boolean' &&
    typeof value.generationTrace.rawPresentationAuthoredByModel === 'boolean'
  )
}

function isAgentIdentity<Role extends 'knowledge-planner' | 'semantic-critic'>(
  value: unknown,
  role: Role,
): value is { role: Role; provider: string; model: string } {
  return (
    isRecord(value) &&
    value.role === role &&
    isSafeReceiptIdentifier(value.provider, 128) &&
    isSafeReceiptIdentifier(value.model, 256)
  )
}

function isCriticFinding(value: unknown): boolean {
  return (
    isRecord(value) &&
    (value.code === 'semantic-mismatch' ||
      value.code === 'ux-quality-gap' ||
      value.code === 'knowledge-gap') &&
    isSafeReceiptText(value.message, 4_096) &&
    isSafeReceiptIdentifierArray(value.evidenceRefs, 64, 1_024)
  )
}

function isPlatformAgentReceipt(
  value: unknown,
  role: 'knowledge-planner' | 'semantic-critic',
): value is Record<string, unknown> & { servedModelId: string } {
  return (
    hasExactKeys(value, [
      'role',
      'provider',
      'requestedModel',
      'servedModelId',
      'reviewRunId',
      'sourceHashes',
      'promptTemplateHash',
      'inputHash',
      'outputHash',
      'status',
      'reasonCodes',
      'selectedCandidateIds',
    ]) &&
    value.role === role &&
    value.provider === 'vllm' &&
    isSafeReceiptText(value.requestedModel, 512) &&
    value.servedModelId === value.requestedModel &&
    isSafeReceiptIdentifier(value.reviewRunId, 256) &&
    isRecord(value.sourceHashes) &&
    Object.keys(value.sourceHashes).length ===
      (role === 'knowledge-planner' ? 1 : 3) &&
    Object.keys(value.sourceHashes).every(
      (key) => key.length > 0 && key.length <= 128,
    ) &&
    Object.values(value.sourceHashes).every(isReceiptHash) &&
    isReceiptHash(value.promptTemplateHash) &&
    isReceiptHash(value.inputHash) &&
    isReceiptHash(value.outputHash) &&
    value.status === 'pass' &&
    isSafeReceiptTextArray(value.reasonCodes, 16, 128) &&
    value.reasonCodes.length > 0 &&
    isSafeReceiptIdentifierArray(value.selectedCandidateIds, 64, 1_024) &&
    value.selectedCandidateIds.length > 0
  )
}

function isPlatformSelection(value: unknown): boolean {
  return (
    hasExactKeys(value, [
      'schemaVersion',
      'normalizedIntent',
      'applicationBlueprintRef',
      'uxPatternRefs',
      'designOntologyReleaseRef',
      'reasons',
      'traceId',
    ]) &&
    value.schemaVersion === 'cogo.library-rag-selection.v1' &&
    isSafeReceiptText(value.normalizedIntent, 4_096) &&
    isSafeReceiptIdentifier(value.applicationBlueprintRef, 1_024) &&
    isSafeReceiptIdentifierArray(value.uxPatternRefs, 64, 1_024) &&
    value.uxPatternRefs.length > 0 &&
    isSafeReceiptIdentifier(value.designOntologyReleaseRef, 1_024) &&
    isSafeReceiptTextArray(value.reasons, 16, 4_096) &&
    value.reasons.length > 0 &&
    isSafeReceiptIdentifier(value.traceId, 256)
  )
}

function isPlatformRequestSubject(value: unknown): boolean {
  return (
    hasExactKeys(value, ['target', 'locale', 'applicationInstanceId']) &&
    value.target === 'web' &&
    isSafeReceiptIdentifier(value.locale, 64) &&
    isSafeReceiptIdentifier(value.applicationInstanceId, 256)
  )
}

function isPlatformPlanReceipt(value: unknown): boolean {
  if (!hasExactKeys(value, [
    'schemaVersion',
    'request',
    'requestHash',
    'scopeHash',
    'bundleContentHash',
    'selection',
    'planner',
    'contentHash',
    'signature',
  ])) {
    return false
  }
  const planner = value.planner
  if (
    value.schemaVersion !== 'cogo.library-rag-plan-receipt.v1' ||
    !isPlatformRequestSubject(value.request) ||
    !isReceiptHash(value.requestHash) ||
    !isReceiptHash(value.scopeHash) ||
    !isReceiptHash(value.bundleContentHash) ||
    !isPlatformSelection(value.selection) ||
    !isRecord(planner) ||
    !isRecord(planner.identity) ||
    !isRecord(planner.receipt) ||
    !hasExactKeys(planner, ['identity', 'receipt']) ||
    !isAgentIdentity(planner.identity, 'knowledge-planner') ||
    planner.identity.provider !== 'vllm' ||
    !isPlatformAgentReceipt(planner.receipt, 'knowledge-planner') ||
    planner.identity.model !== planner.receipt.servedModelId ||
    !isReceiptHash(value.contentHash) ||
    !hasExactKeys(value.signature, ['algorithm', 'keyId', 'value']) ||
    value.signature.algorithm !== 'hmac-sha256' ||
    !isReceiptSigningKeyId(value.signature.keyId) ||
    typeof value.signature.value !== 'string' ||
    value.signature.value.length < 44 ||
    value.signature.value.length > 88 ||
    !/^[A-Za-z0-9+/]+={0,2}$/.test(value.signature.value)
  ) {
    return false
  }
  const { contentHash, signature: _signature, ...unsigned } = value
  return hashCanonicalContent(unsigned) === contentHash
}

function isPlatformCriticReceipt(value: unknown): boolean {
  if (!hasExactKeys(value, [
    'schemaVersion',
    'request',
    'requestHash',
    'scopeHash',
    'bundleContentHash',
    'planReceiptContentHash',
    'compiledArtifactContentHash',
    'selection',
    'critic',
    'contentHash',
    'signature',
  ])) {
    return false
  }
  const critic = value.critic
  if (
    value.schemaVersion !== 'cogo.library-rag-critic-receipt.v1' ||
    !isPlatformRequestSubject(value.request) ||
    !isReceiptHash(value.requestHash) ||
    !isReceiptHash(value.scopeHash) ||
    !isReceiptHash(value.bundleContentHash) ||
    !isReceiptHash(value.planReceiptContentHash) ||
    !isReceiptHash(value.compiledArtifactContentHash) ||
    !isPlatformSelection(value.selection) ||
    !isRecord(critic) ||
    !isRecord(critic.identity) ||
    !isRecord(critic.verdict) ||
    !isRecord(critic.receipt) ||
    !hasExactKeys(critic, ['identity', 'verdict', 'receipt']) ||
    !isAgentIdentity(critic.identity, 'semantic-critic') ||
    critic.identity.provider !== 'vllm' ||
    !hasExactKeys(critic.verdict, [
      'schemaVersion',
      'verdict',
      'findings',
      'traceId',
    ]) ||
    critic.verdict.schemaVersion !==
      'cogo.library-semantic-critic-verdict.v1' ||
    critic.verdict.verdict !== 'pass' ||
    !Array.isArray(critic.verdict.findings) ||
    critic.verdict.findings.length !== 0 ||
    !isSafeReceiptIdentifier(critic.verdict.traceId, 256) ||
    !isPlatformAgentReceipt(critic.receipt, 'semantic-critic') ||
    critic.identity.model !== critic.receipt.servedModelId ||
    !isReceiptHash(value.contentHash) ||
    !hasExactKeys(value.signature, ['algorithm', 'keyId', 'value']) ||
    value.signature.algorithm !== 'hmac-sha256' ||
    !isReceiptSigningKeyId(value.signature.keyId) ||
    typeof value.signature.value !== 'string' ||
    value.signature.value.length < 44 ||
    value.signature.value.length > 88 ||
    !/^[A-Za-z0-9+/]+={0,2}$/.test(value.signature.value)
  ) {
    return false
  }
  const { contentHash, signature: _signature, ...unsigned } = value
  return hashCanonicalContent(unsigned) === contentHash
}

function isReceiptSigningKeyId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value.length <= 128 &&
    /^[A-Za-z0-9][A-Za-z0-9_.:-]*$/.test(value)
  )
}

/**
 * The L1 Design Ontology binding record the receipt claims backs this artifact.
 *
 * Every field of it reaches a Studio surface, so every field goes through the
 * same identifier/text discipline as the rest of the receipt. The key set is
 * CLOSED: an unrecognised sibling here is a receipt this Studio build does not
 * understand, and rendering it would be rendering something unvalidated.
 * Forward-compatible additions (e.g. the Task 7 usage fields) belong on the
 * evidence record as optional siblings, not inside this closed record.
 */
function isLibraryDesignBindingEvidence(value: unknown): boolean {
  return (
    hasExactKeys(value, [
      'shardRef',
      'applicationRef',
      'designOntologyReleaseRef',
      'requiredTokenRefs',
    ]) &&
    isSafeReceiptIdentifier(value.shardRef, 512) &&
    isSafeReceiptIdentifier(value.applicationRef, 512) &&
    isSafeReceiptIdentifier(value.designOntologyReleaseRef, 512) &&
    isSafeReceiptIdentifierArray(value.requiredTokenRefs, 256, 512)
  )
}

/** One immutable read of one ontology shard, as the retrieval reported it. */
function isKnowledgeShardEnvelope(value: unknown): boolean {
  return (
    hasExactKeys(value, [
      'shardRef',
      'admissionGate',
      'root',
      'assetRefs',
      'contentHash',
    ]) &&
    isSafeReceiptIdentifier(value.shardRef, 512) &&
    typeof value.admissionGate === 'string' &&
    KNOWLEDGE_SHARD_ADMISSION_GATES.has(value.admissionGate) &&
    hasExactKeys(value.root, ['kind', 'ref']) &&
    typeof value.root.kind === 'string' &&
    KNOWLEDGE_SHARD_ROOT_KINDS.has(value.root.kind) &&
    isSafeReceiptIdentifier(value.root.ref, 512) &&
    isSafeReceiptIdentifierArray(value.assetRefs, 512, 512) &&
    isReceiptHash(value.contentHash)
  )
}

/**
 * Retrieval provenance: which shard envelopes the bundle was assembled from.
 * Its trace id must be the receipt's own — a provenance record pointing at a
 * different retrieval is not this receipt's provenance.
 */
function isKnowledgeProvenance(
  value: unknown,
  retrievalTraceId: string,
): boolean {
  return (
    hasExactKeys(value, [
      'schemaVersion',
      'retrievalTraceId',
      'shards',
      'designationRecordRefs',
      'bundleContentHash',
    ]) &&
    value.schemaVersion === 'cogo.library-knowledge-bundle-provenance.v1' &&
    isSafeReceiptIdentifier(value.retrievalTraceId, 256) &&
    value.retrievalTraceId === retrievalTraceId &&
    isRecordArray(value.shards, isKnowledgeShardEnvelope, 256) &&
    isSafeReceiptIdentifierArray(value.designationRecordRefs, 256, 512) &&
    isReceiptHash(value.bundleContentHash)
  )
}

function isEvidence(
  value: unknown,
): value is SuccessfulLibraryRagEvidence {
  return (
    isRecord(value) &&
    value.schemaVersion === 'cogo.library-rag-orchestration-evidence.v1' &&
    isSafeReceiptIdentifier(value.retrievalTraceId, 256) &&
    isRecord(value.request) &&
    value.request.target === 'web' &&
    isSafeReceiptIdentifier(value.request.locale, 64) &&
    isSafeReceiptIdentifier(value.request.applicationInstanceId, 256) &&
    isRecord(value.planner) &&
    isAgentIdentity(value.planner.identity, 'knowledge-planner') &&
    isSafeReceiptIdentifier(value.planner.traceId, 256) &&
    (value.planner.authorityReceipt === undefined ||
      isPlatformPlanReceipt(value.planner.authorityReceipt)) &&
    isRecord(value.selection) &&
    value.selection.schemaVersion === 'cogo.library-rag-selection.v1' &&
    isSafeReceiptText(value.selection.normalizedIntent, 4_096) &&
    isSafeReceiptIdentifier(value.selection.applicationBlueprintRef, 512) &&
    isSafeReceiptIdentifierArray(value.selection.uxPatternRefs, 256, 512) &&
    value.selection.uxPatternRefs.length > 0 &&
    isSafeReceiptIdentifier(value.selection.designOntologyReleaseRef, 512) &&
    isSafeReceiptTextArray(value.selection.reasons, 64, 2_048) &&
    isSafeReceiptIdentifier(value.selection.traceId, 256) &&
    // Both fields are REQUIRED to be present and either `null` or well formed.
    // `null` is a positive statement ("this artifact is not bound to the
    // Library's designated release" / "the gateway declared no provenance"),
    // so an absent key is not the same thing and is not admitted.
    'designBinding' in value &&
    (value.designBinding === null ||
      isLibraryDesignBindingEvidence(value.designBinding)) &&
    'knowledgeProvenance' in value &&
    (value.knowledgeProvenance === null ||
      isKnowledgeProvenance(value.knowledgeProvenance, value.retrievalTraceId)) &&
    isRecord(value.compiler) &&
    isSafeReceiptIdentifier(value.compiler.designOntologyReleaseRef, 512) &&
    isReceiptHash(value.compiler.applicationSemanticHash) &&
    isReceiptHash(value.compiler.designBindingHash) &&
    isReceiptHash(value.compiler.renderContractHash) &&
    isRecord(value.critic) &&
    isAgentIdentity(value.critic.identity, 'semantic-critic') &&
    (value.critic.verdict === 'pass' || value.critic.verdict === 'fail') &&
    isRecordArray(value.critic.findings, isCriticFinding, 256) &&
    isSafeReceiptIdentifier(value.critic.traceId, 256) &&
    (value.critic.authorityReceipt === undefined ||
      isPlatformCriticReceipt(value.critic.authorityReceipt))
  )
}

function hasPlatformAuthorityReceiptPair(
  resolution: SuccessfulLibraryRagReceipt,
): boolean {
  const { evidence, artifact } = resolution
  const plan = evidence.planner.authorityReceipt
  const critic = evidence.critic.authorityReceipt
  const runtimeAuthority =
    classifyStudioLibraryRagRuntimeAuthority(resolution)
  if (runtimeAuthority === 'admitted-legacy') {
    return plan === undefined && critic === undefined
  }
  if (
    runtimeAuthority !== 'admitted-platform' ||
    plan === undefined ||
    critic === undefined ||
    !isPlatformPlanReceipt(plan) ||
    !isPlatformCriticReceipt(critic)
  ) {
    return false
  }
  const request = evidence.request
  const selection = evidence.selection
  const selectedRefs = [
    selection.applicationBlueprintRef,
    ...selection.uxPatternRefs,
    selection.designOntologyReleaseRef,
  ]
  try {
    return (
      plan.request.target === request.target &&
      plan.request.locale === request.locale &&
      plan.request.applicationInstanceId === request.applicationInstanceId &&
      critic.request.target === request.target &&
      critic.request.locale === request.locale &&
      critic.request.applicationInstanceId === request.applicationInstanceId &&
      hashCanonicalContent(plan.selection) ===
        hashCanonicalContent(selection) &&
      hashCanonicalContent(critic.selection) ===
        hashCanonicalContent(selection) &&
      plan.planner.identity.provider === evidence.planner.identity.provider &&
      plan.planner.identity.model === evidence.planner.identity.model &&
      plan.selection.traceId === evidence.planner.traceId &&
      plan.planner.receipt.reviewRunId === evidence.planner.traceId &&
      critic.critic.identity.provider === evidence.critic.identity.provider &&
      critic.critic.identity.model === evidence.critic.identity.model &&
      critic.critic.verdict.traceId === evidence.critic.traceId &&
      critic.critic.receipt.reviewRunId === evidence.critic.traceId &&
      sameOrderedValues(
        plan.planner.receipt.selectedCandidateIds,
        selectedRefs,
      ) &&
      sameOrderedValues(
        critic.critic.receipt.selectedCandidateIds,
        selectedRefs,
      ) &&
      critic.requestHash === plan.requestHash &&
      critic.scopeHash === plan.scopeHash &&
      critic.bundleContentHash === plan.bundleContentHash &&
      critic.planReceiptContentHash === plan.contentHash &&
      critic.compiledArtifactContentHash === hashCanonicalContent(artifact)
    )
  } catch {
    return false
  }
}

function isUnique(values: readonly string[]): boolean {
  return new Set(values).size === values.length
}

function sameRefs(left: readonly string[], right: readonly string[]): boolean {
  return (
    isUnique(left) &&
    isUnique(right) &&
    left.length === right.length &&
    [...left].sort().every((ref, index) => ref === [...right].sort()[index])
  )
}

function sameOrderedValues(
  actual: readonly string[],
  projected: readonly string[],
): boolean {
  return (
    actual.length === projected.length &&
    actual.every((value, index) => value === projected[index])
  )
}

function hasCanonicalSourceInteractionAuthority(
  input: Record<string, unknown>,
  subject: {
    applicationBlueprintRef: string
    applicationInstanceId: string
    uxPatternRefs: string[]
    designOntologyReleaseRef: string
    designSourceRefs: string[]
    admittedEvidenceRefs: string[]
  },
): boolean {
  const relationSet = input.sourceInteractionRelationSet
  const relations = input.sourceInteractionRelations

  if (relationSet === undefined) {
    return relations === undefined
  }
  if (
    !isCanonicalSourceInteractionRelationSet(relationSet) ||
    !Array.isArray(relations)
  ) {
    return false
  }

  const admittedEvidenceRefs = new Set(subject.admittedEvidenceRefs)
  try {
    return (
      relationSet.applicationBlueprintRef ===
        subject.applicationBlueprintRef &&
      relationSet.applicationInstanceId === subject.applicationInstanceId &&
      relationSet.designOntologyReleaseRef ===
        subject.designOntologyReleaseRef &&
      sameRefs(relationSet.uxPatternRefs, subject.uxPatternRefs) &&
      sameRefs(relationSet.designSourceRefs, subject.designSourceRefs) &&
      relationSet.evidenceRefs.every((ref) => admittedEvidenceRefs.has(ref)) &&
      hashCanonicalContent(relationSet.relations) ===
        hashCanonicalContent(relations)
    )
  } catch {
    return false
  }
}

function uniqueInOrder(values: readonly string[]): string[] {
  return [...new Set(values)]
}

function hasClosedFallbackTraces(
  artifact: SuccessfulLibraryRagArtifact,
): boolean {
  return (
    artifact.library.generationTrace.legacyStudioArtifactUsed === false &&
    artifact.library.generationTrace.genericFallbackUsed === false &&
    artifact.library.generationTrace.rawPresentationAuthoredByModel === false &&
    artifact.design.generationTrace.rawPresentationAuthoredByModel === false &&
    artifact.design.generationTrace.genericFallbackUsed === false &&
    artifact.design.generationTrace.generatedOutputAdmissionAllowed === false &&
    artifact.generationTrace.genericFallbackUsed === false &&
    artifact.generationTrace.rawPresentationAuthoredByModel === false
  )
}

function invalid(
  gapCode: StudioLibraryRagReceiptGapCode = 'receipt-invalid',
): StudioLibraryRagReceiptValidation {
  return { ok: false, gapCode }
}

export function validateStudioLibraryRagSuccessReceipt(
  value: unknown,
): StudioLibraryRagReceiptValidation {
  if (!isRecord(value) || value.ok !== true) {
    return invalid()
  }
  const { input, designOntologyRelease, artifact, evidence } = value
  if (
    !isRecord(input) ||
    !isRecord(input.library) ||
    !isRecord(input.libraryAuthority) ||
    !isSafeReceiptIdentifier(input.designOntologyReleaseRef, 512) ||
    !isDesignBoundArtifact(artifact, input.sourceInteractionRelations) ||
    !isEvidence(evidence)
  ) {
    return invalid()
  }

  const libraryAdmission = admitLibraryComposition(
    input.library,
    input.libraryAuthority,
  )
  if (!libraryAdmission.ok) {
    return invalid()
  }

  const compositionAdmission = admitApplicationCompositionInputs(
    artifact.library.plan,
    designOntologyRelease,
  )
  if (!compositionAdmission.ok) {
    return invalid()
  }

  const typedInput = libraryAdmission.input
  const release = compositionAdmission.input.release
  const typedArtifact = artifact
  const typedEvidence = evidence
  const blueprint = typedInput.applicationBlueprint
  const blueprintRef =
    `${blueprint.applicationBlueprintId}@${blueprint.version}`
  const inputUxPatternRefs = typedInput.uxPatterns.map(
    (pattern) => `${pattern.uxPatternId}@${pattern.version}`,
  )
  const artifactUxPatternRefs = typedArtifact.library.provenance.uxPatternRefs
  const selectionUxPatternRefs = typedEvidence.selection.uxPatternRefs
  const runtime = typedArtifact.library.runtime
  const design = typedArtifact.design
  const plan = typedArtifact.library.plan

  if (
    !hasCanonicalSourceInteractionAuthority(input, {
      applicationBlueprintRef: blueprintRef,
      applicationInstanceId: typedInput.applicationInstanceId,
      uxPatternRefs: inputUxPatternRefs,
      designOntologyReleaseRef: release.releaseId,
      designSourceRefs: release.sourceRefs,
      admittedEvidenceRefs: [
        ...typedInput.applicationBlueprint.lineage.sourceRefs,
        ...typedInput.uxPatterns.flatMap((pattern) =>
          pattern.lineage.sourceRefs,
        ),
        ...(isStringArray(input.libraryAuthority.admittedUxResearchRefs)
          ? input.libraryAuthority.admittedUxResearchRefs
          : []),
        ...(isStringArray(input.libraryAuthority.approvedCanonicalSourceRefs)
          ? input.libraryAuthority.approvedCanonicalSourceRefs
          : []),
        ...(isStringArray(input.libraryAuthority.admittedApplicationCoreRefs)
          ? input.libraryAuthority.admittedApplicationCoreRefs
          : []),
      ],
    })
  ) {
    return invalid()
  }

  if (!hasClosedFallbackTraces(typedArtifact)) {
    return invalid('fallback-trace-open')
  }

  if (
    typedInput.applicationInstanceId !==
      typedArtifact.library.applicationInstanceId ||
    typedInput.applicationInstanceId !==
      typedEvidence.request.applicationInstanceId ||
    input.designOntologyReleaseRef !== release.releaseId ||
    input.designOntologyReleaseRef !==
      typedArtifact.designOntologyReleaseRef ||
    blueprintRef !== typedArtifact.library.applicationBlueprintRef ||
    blueprintRef !==
      typedArtifact.library.provenance.applicationBlueprintRef ||
    blueprint.applicationCoreRef !==
      typedArtifact.library.provenance.applicationCoreRef ||
    blueprint.applicationCoreRef !== plan.applicationCoreRef ||
    blueprint.recipeRef !== plan.recipeRef ||
    blueprint.target !== plan.target ||
    blueprint.locale !== plan.locale ||
    blueprint.locale !== typedEvidence.request.locale ||
    blueprint.userGoal !== runtime.userGoal ||
    !sameRefs(inputUxPatternRefs, artifactUxPatternRefs) ||
    typedArtifact.designOntologyReleaseRef !== design.designReleaseRef ||
    release.target !== design.target ||
    plan.planId !== design.planId ||
    plan.applicationCoreRef !== design.applicationCoreRef ||
    plan.target !== design.target ||
    plan.locale !== design.locale ||
    plan.recipeRef !== design.recipeRef ||
    !sameOrderedValues(plan.states, design.states) ||
    !sameOrderedValues(
      runtime.pageIds,
      runtime.pages.map((page) => page.pageId),
    ) ||
    !sameOrderedValues(
      runtime.blockIds,
      runtime.blocks.map((block) => block.blockId),
    ) ||
    !sameOrderedValues(
      runtime.actionIds,
      uniqueInOrder([
        ...runtime.uxStatecharts.flatMap((statechart) =>
          statechart.transitions.flatMap(
            (transition) => transition.effectActionIds,
          ),
        ),
        ...runtime.semanticActions.map((action) => action.actionId),
      ]),
    ) ||
    !sameOrderedValues(
      runtime.resultIds,
      runtime.results.map((result) => result.resultId),
    ) ||
    !sameOrderedValues(
      typedArtifact.library.resolvedApplicationFlowIds,
      runtime.applicationFlows.map((flow) => flow.flowId),
    )
  ) {
    return invalid()
  }

  if (
    typedEvidence.selection.applicationBlueprintRef !==
      typedArtifact.library.applicationBlueprintRef
  ) {
    return invalid('application-blueprint-ref-mismatch')
  }
  if (!sameRefs(selectionUxPatternRefs, artifactUxPatternRefs)) {
    return invalid('ux-pattern-refs-mismatch')
  }
  if (
    typedEvidence.selection.designOntologyReleaseRef !==
      typedArtifact.designOntologyReleaseRef
  ) {
    return invalid('design-release-ref-mismatch')
  }
  if (typedEvidence.selection.normalizedIntent !== runtime.userGoal) {
    return invalid('normalized-intent-mismatch')
  }
  if (typedEvidence.critic.verdict !== 'pass') {
    return invalid('critic-verdict-not-pass')
  }
  if (
    typedEvidence.compiler.designOntologyReleaseRef !==
      typedArtifact.designOntologyReleaseRef ||
    typedEvidence.compiler.applicationSemanticHash !==
      typedArtifact.applicationSemanticHash ||
    typedEvidence.compiler.designBindingHash !==
      typedArtifact.designBindingHash ||
    typedEvidence.compiler.renderContractHash !==
      typedArtifact.renderContractHash
  ) {
    return invalid('compiler-artifact-mismatch')
  }
  if (typedEvidence.planner.traceId !== typedEvidence.selection.traceId) {
    return invalid()
  }
  if (
    !hasPlatformAuthorityReceiptPair(
      value as unknown as SuccessfulLibraryRagReceipt,
    )
  ) {
    return invalid()
  }

  const admittedCriticEvidenceRefs = new Set([
    typedArtifact.library.applicationBlueprintRef,
    ...artifactUxPatternRefs,
    ...typedArtifact.library.provenance.sourceRefs,
    typedArtifact.designOntologyReleaseRef,
  ])
  if (
    !typedEvidence.critic.findings.every((finding) =>
      finding.evidenceRefs.every((ref) =>
        admittedCriticEvidenceRefs.has(ref),
      ),
    )
  ) {
    return invalid()
  }

  return {
    ok: true,
    receipt: value as unknown as SuccessfulLibraryRagReceipt,
  }
}
